import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  Loader2,
  Maximize2,
  Minimize2,
  SendHorizontal,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  DASHBOARD_CHAT_METRICS,
  DASHBOARD_CHAT_VENUES,
  dashboardChatService,
} from '../../apis/dashboardChatService';
import { useDashboardChat } from '../../context/DashboardChatContext';
import { useAuth } from '../../context/AuthContext';
import { isManagementRole } from '../../constants/roles';
import eplLogo from '../../asset/img/logo.png';

const SUGGESTIONS = [
  'Which venue is leading this month?',
  'How many cash tables did EPL run last week?',
  'Show tournament totals for both venues this month',
  'What are the top games by hour?',
];

const formatNumber = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return String(value ?? '—');
  return num.toLocaleString();
};

const formatHour = (hour) => {
  const h = Number(hour);
  if (Number.isNaN(h)) return String(hour ?? '—');
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:00 ${suffix}`;
};

const getVenueLists = (result) => ({
  epl: Array.isArray(result?.epl) ? result.epl : [],
  tch: Array.isArray(result?.competitor)
    ? result.competitor
    : Array.isArray(result?.tch)
      ? result.tch
      : [],
});

const VenueScalarCards = ({ result, status }) => {
  const epl = result.epl_total ?? (typeof result.epl === 'number' ? result.epl : null);
  const tch =
    result.tch_total ??
    (typeof result.competitor === 'number' ? result.competitor : null) ??
    (typeof result.tch === 'number' ? result.tch : null);

  if (epl == null && tch == null) return null;

  const leading = status
    ? String(status).toLowerCase().includes('epl')
      ? 'epl'
      : String(status).toLowerCase().includes('tch')
        ? 'tch'
        : 'tie'
    : epl > tch
      ? 'epl'
      : tch > epl
        ? 'tch'
        : 'tie';

  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      <div className={`rounded-lg border px-2.5 py-2 ${leading === 'epl' ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'}`}>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">EPL</div>
        <div className="text-lg font-bold text-slate-900">{formatNumber(epl ?? 0)}</div>
      </div>
      <div className={`rounded-lg border px-2.5 py-2 ${leading === 'tch' ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-white'}`}>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-rose-700">TCH</div>
        <div className="text-lg font-bold text-slate-900">{formatNumber(tch ?? 0)}</div>
      </div>
      {(status || result.difference != null) && (
        <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">
            {status ? 'Status' : 'Difference'}
          </div>
          <div className="text-sm font-semibold text-slate-900">
            {status || `Δ ${formatNumber(result.difference)}`}
            {status && result.difference != null && (
              <span className="ml-2 text-xs font-medium text-slate-600">
                Δ {formatNumber(result.difference)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DayWiseStats = ({ result }) => {
  const { epl, tch } = getVenueLists(result);
  const dates = Array.from(
    new Set([
      ...epl.map((row) => row.business_date),
      ...tch.map((row) => row.business_date),
    ].filter(Boolean))
  ).sort();

  const eplMap = Object.fromEntries(epl.map((row) => [row.business_date, row.total_tables]));
  const tchMap = Object.fromEntries(tch.map((row) => [row.business_date, row.total_tables]));

  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="grid grid-cols-[1.2fr_0.7fr_0.7fr] gap-1 border-b border-slate-100 bg-slate-50 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <span>Date</span>
        <span className="text-right text-blue-700">EPL</span>
        <span className="text-right text-rose-700">TCH</span>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {dates.map((date) => (
          <div
            key={date}
            className="grid grid-cols-[1.2fr_0.7fr_0.7fr] gap-1 border-b border-slate-50 px-2.5 py-1.5 text-xs last:border-0"
          >
            <span className="font-medium text-slate-700">{date}</span>
            <span className="text-right font-semibold text-blue-700">{formatNumber(eplMap[date] ?? 0)}</span>
            <span className="text-right font-semibold text-rose-700">{formatNumber(tchMap[date] ?? 0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TopGamesByHourStats = ({ result }) => {
  const { epl, tch } = getVenueLists(result);

  const renderVenueBlock = (label, rows, tone) => {
    const sorted = [...rows].sort((a, b) => {
      const tablesDiff = Number(b.total_tables || 0) - Number(a.total_tables || 0);
      if (tablesDiff !== 0) return tablesDiff;
      return Number(a.hour_of_day || 0) - Number(b.hour_of_day || 0);
    });

    const byHour = sorted.reduce((acc, row) => {
      const key = row.hour_of_day ?? '—';
      if (!acc[key]) acc[key] = [];
      acc[key].push(row);
      return acc;
    }, {});

    const hours = Object.keys(byHour).sort((a, b) => Number(a) - Number(b));
    const toneClasses =
      tone === 'epl'
        ? 'border-blue-200 bg-blue-50/60'
        : 'border-rose-200 bg-rose-50/60';
    const titleTone = tone === 'epl' ? 'text-blue-800' : 'text-rose-800';

    return (
      <div className={`rounded-xl border ${toneClasses}`}>
        <div className={`flex items-center justify-between px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide ${titleTone}`}>
          <span>{label}</span>
          <span className="rounded-full bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
            {rows.length} rows
          </span>
        </div>
        <div className="max-h-44 space-y-2 overflow-y-auto px-2 pb-2">
          {hours.length === 0 && (
            <div className="rounded-lg bg-white px-2 py-2 text-xs text-slate-500">No rows</div>
          )}
          {hours.map((hour) => (
            <div key={`${label}-${hour}`} className="overflow-hidden rounded-lg border border-white/70 bg-white">
              <div className="border-b border-slate-100 bg-slate-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                {formatHour(hour)}
              </div>
              {byHour[hour].slice(0, 8).map((row, idx) => (
                <div
                  key={`${row.game_name}-${idx}`}
                  className="flex items-center justify-between gap-2 border-b border-slate-50 px-2 py-1.5 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="truncate text-xs font-medium text-slate-800" title={row.game_name}>
                      {row.game_name}
                    </div>
                  </div>
                  <div className="shrink-0 rounded-md bg-slate-900 px-1.5 py-0.5 text-[11px] font-bold text-amber-300">
                    {formatNumber(row.total_tables || 0)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-2 space-y-2">
      {renderVenueBlock('EPL', epl, 'epl')}
      {renderVenueBlock('TCH', tch, 'tch')}
    </div>
  );
};

const GameTypeStats = ({ rows }) => {
  const sorted = [...rows].sort(
    (a, b) => Math.abs(Number(b.difference || 0)) - Math.abs(Number(a.difference || 0))
  );

  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="grid grid-cols-[1.4fr_0.55fr_0.55fr_0.55fr] gap-1 border-b border-slate-100 bg-slate-50 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <span>Game</span>
        <span className="text-right text-blue-700">EPL</span>
        <span className="text-right text-rose-700">TCH</span>
        <span className="text-right">Δ</span>
      </div>
      <div className="max-h-52 overflow-y-auto">
        {sorted.slice(0, 20).map((row) => {
          const diff = Number(row.difference || 0);
          return (
            <div
              key={`${row.game_name}-${row.tag}`}
              className="grid grid-cols-[1.4fr_0.55fr_0.55fr_0.55fr] gap-1 border-b border-slate-50 px-2.5 py-1.5 text-xs last:border-0"
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-800" title={row.game_name}>
                  {row.game_name}
                </div>
                {row.tag && (
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">{row.tag}</div>
                )}
              </div>
              <span className="text-right font-semibold text-blue-700">{formatNumber(row.epl || 0)}</span>
              <span className="text-right font-semibold text-rose-700">{formatNumber(row.competitor || 0)}</span>
              <span
                className={`text-right font-semibold ${
                  diff > 0 ? 'text-emerald-700' : diff < 0 ? 'text-rose-700' : 'text-slate-500'
                }`}
              >
                {diff > 0 ? '+' : ''}
                {formatNumber(diff)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ResultInsight = ({ result, params }) => {
  if (!result) return null;

  const metric = params?.metric;

  if (Array.isArray(result)) {
    if (result[0]?.game_name != null && (result[0]?.epl != null || result[0]?.competitor != null)) {
      return <GameTypeStats rows={result} />;
    }
    return (
      <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2">
        {result.slice(0, 25).map((row, idx) => (
          <div key={idx} className="rounded-lg bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
            {typeof row === 'object' ? (
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {Object.entries(row).map(([key, value]) => (
                  <span key={key}>
                    <span className="text-slate-400">{key.replace(/_/g, ' ')}:</span>{' '}
                    <span className="font-semibold">{formatNumber(value)}</span>
                  </span>
                ))}
              </div>
            ) : (
              String(row)
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof result !== 'object') {
    return (
      <div className="mt-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm font-semibold text-slate-900">
        {formatNumber(result)}
      </div>
    );
  }

  if (
    metric === 'top_games_by_hour' ||
    (Array.isArray(result.epl) && result.epl[0]?.hour_of_day != null) ||
    (Array.isArray(result.competitor) && result.competitor[0]?.hour_of_day != null)
  ) {
    return <TopGamesByHourStats result={result} />;
  }

  if (
    metric === 'day_wise_breakdown' ||
    (Array.isArray(result.epl) && result.epl[0]?.business_date != null) ||
    (Array.isArray(result.competitor) && result.competitor[0]?.business_date != null)
  ) {
    return <DayWiseStats result={result} />;
  }

  if (
    metric === 'competition_status' ||
    result.status ||
    typeof result.epl === 'number' ||
    typeof result.competitor === 'number' ||
    result.epl_total != null
  ) {
    return <VenueScalarCards result={result} status={result.status} />;
  }

  const scalarEntries = Object.entries(result).filter(
    ([, value]) => value == null || ['string', 'number', 'boolean'].includes(typeof value)
  );

  if (scalarEntries.length) {
    return (
      <div className="mt-2 grid grid-cols-2 gap-2">
        {scalarEntries.slice(0, 8).map(([key, value]) => (
          <div key={key} className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
            <div className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {key.replace(/_/g, ' ')}
            </div>
            <div className="text-sm font-bold text-slate-900">{formatNumber(value)}</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <pre className="mt-2 max-h-36 overflow-auto rounded-lg bg-slate-900/90 p-2 text-[10px] leading-relaxed text-amber-100">
      {JSON.stringify(result, null, 2)}
    </pre>
  );
};

const BotMessage = ({ message }) => (
  <div className="flex gap-2.5 pr-4 animate-[fadeInUp_0.28s_ease-out]">
    <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-white to-slate-100 shadow-[0_4px_14px_rgba(15,23,42,0.12)] ring-1 ring-amber-300/50">
      <img src={eplLogo} alt="EPL" className="h-5 w-5 object-contain" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="rounded-2xl rounded-tl-md border border-white/70 bg-white/90 px-3.5 py-2.5 text-sm text-slate-800 shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur">
        <p className="whitespace-pre-wrap leading-relaxed">{message.text}</p>
        {message.payload?.params && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {message.payload.params.startDate && (
              <span className="rounded-full border border-slate-200/80 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                {message.payload.params.startDate} → {message.payload.params.endDate}
              </span>
            )}
            {message.payload.params.venue && (
              <span className="rounded-full border border-slate-200/80 bg-slate-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                {message.payload.params.venue}
              </span>
            )}
            {message.payload.params.metric && (
              <span className="rounded-full border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                {message.payload.params.metric.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        )}
        {message.payload?.result && (
          <ResultInsight result={message.payload.result} params={message.payload.params} />
        )}
      </div>
    </div>
  </div>
);

const UserMessage = ({ message }) => (
  <div className="flex justify-end pl-10 animate-[fadeInUp_0.28s_ease-out]">
    <div className="rounded-2xl rounded-tr-md bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-3.5 py-2.5 text-sm text-white shadow-[0_10px_24px_rgba(15,23,42,0.25)] ring-1 ring-white/10">
      <p className="whitespace-pre-wrap leading-relaxed">{message.text}</p>
    </div>
  </div>
);

export default function DashboardChatBot() {
  const { currentUser } = useAuth();
  const canUseChat = isManagementRole(String(currentUser?.role || '').trim());
  const { isOpen, closeChat, openChat, queryDefaults } = useDashboardChat();
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'bot',
      text: 'Ask me about EPL vs TCH performance, table counts, tournaments, or game trends. You can also run a structured query below.',
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [queryForm, setQueryForm] = useState(queryDefaults);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setQueryForm((prev) => ({
      ...prev,
      startDate: queryDefaults.startDate,
      endDate: queryDefaults.endDate,
    }));
  }, [queryDefaults.endDate, queryDefaults.startDate]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 180);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const appendBotReply = useCallback((payload, fallbackError) => {
    if (!payload?.ok) {
      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-err`,
          role: 'bot',
          text: payload?.error || fallbackError || 'Something went wrong. Please try again.',
        },
      ]);
      return;
    }

    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}-bot`,
        role: 'bot',
        text: payload.summary || 'Here is what I found.',
        payload,
      },
    ]);
  }, []);

  const sendMessage = useCallback(
    async (rawText) => {
      const text = (rawText || '').trim();
      if (!text || sending) return;

      setMessages((prev) => [...prev, { id: `${Date.now()}-user`, role: 'user', text }]);
      setInput('');
      setSending(true);

      const response = await dashboardChatService.ask(text);
      appendBotReply(response.data, response.error);
      setSending(false);
    },
    [appendBotReply, sending]
  );

  const runStructuredQuery = useCallback(async () => {
    if (sending) return;
    const label = DASHBOARD_CHAT_METRICS.find((m) => m.value === queryForm.metric)?.label || queryForm.metric;
    const venueLabel = DASHBOARD_CHAT_VENUES.find((v) => v.value === queryForm.venue)?.label || queryForm.venue;

    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}-user`,
        role: 'user',
        text: `Query: ${label} · ${venueLabel} · ${queryForm.startDate} → ${queryForm.endDate}`,
      },
    ]);
    setSending(true);

    const response = await dashboardChatService.query(queryForm);
    appendBotReply(response.data, response.error);
    setSending(false);
  }, [appendBotReply, queryForm, sending]);

  const clearChat = () => {
    setMessages([
      {
        id: 'welcome',
        role: 'bot',
        text: 'Chat cleared. Ask a new analytics question whenever you are ready.',
      },
    ]);
  };

  const panelSize = useMemo(
    () =>
      expanded
        ? 'h-[min(88vh,780px)] w-[min(96vw,540px)]'
        : 'h-[min(76vh,640px)] w-[min(94vw,420px)]',
    [expanded]
  );

  if (!canUseChat) return null;

  if (!isOpen) {
    return (
      <div className="fixed bottom-5 right-5 z-[60]">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(251,191,36,0.35),transparent_68%)] blur-md animate-[botGlow_2.8s_ease-in-out_infinite]" />
        <span className="pointer-events-none absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-300/70 animate-[botRipple_2.4s_ease-out_infinite]" />
        <span className="pointer-events-none absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-400/50 animate-[botRipple_2.4s_ease-out_infinite] [animation-delay:0.8s]" />
        <span className="pointer-events-none absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border border-yellow-200/40 animate-[botRipple_2.4s_ease-out_infinite] [animation-delay:1.6s]" />

        <button
          type="button"
          onClick={openChat}
          className="group relative flex items-center gap-0 overflow-visible rounded-full border border-amber-300/50 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-1.5 text-white shadow-[0_18px_50px_rgba(15,23,42,0.45)] transition-all duration-300 hover:pr-5 hover:shadow-[0_22px_60px_rgba(251,191,36,0.28)] animate-[botNudge_3.2s_ease-in-out_infinite]"
          aria-label="Open analytics assistant"
        >
          <span className="pointer-events-none absolute -inset-[3px] rounded-full bg-[conic-gradient(from_0deg,transparent_0%,rgba(251,191,36,0.95)_18%,transparent_32%,transparent_58%,rgba(250,204,21,0.85)_72%,transparent_88%)] opacity-90 animate-[botSpin_3.5s_linear_infinite]" />
          <span className="pointer-events-none absolute -inset-[1px] rounded-full bg-slate-950" />

          <span className="relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-white via-amber-50 to-yellow-100 shadow-inner ring-2 ring-amber-300/80 animate-[botPulse_2s_ease-in-out_infinite]">
            <img
              src={eplLogo}
              alt="EPL"
              className="h-10 w-10 object-contain transition-transform duration-300 group-hover:scale-110 animate-[botLogoFloat_2.6s_ease-in-out_infinite]"
            />
            <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,1)] ring-2 ring-slate-950 animate-[botDot_1.4s_ease-in-out_infinite]" />
          </span>

          <span className="relative max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-300 group-hover:ml-3 group-hover:max-w-[150px] group-hover:opacity-100 group-hover:pr-2">
            <span className="block text-sm font-semibold tracking-wide">Analytics Bot</span>
          </span>
        </button>

        <div className="pointer-events-none absolute -top-9 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap">
          <div className="rounded-full border border-amber-300/50 bg-slate-950/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-300 shadow-[0_8px_24px_rgba(15,23,42,0.35)] animate-[botLabelFloat_2.4s_ease-in-out_infinite]">
            Ask Bot
          </div>
        </div>

        <style>{`
          @keyframes botRipple {
            0% { transform: translate(-50%, -50%) scale(0.85); opacity: 0.85; }
            70% { transform: translate(-50%, -50%) scale(1.85); opacity: 0; }
            100% { transform: translate(-50%, -50%) scale(1.85); opacity: 0; }
          }
          @keyframes botGlow {
            0%, 100% { opacity: 0.45; transform: translate(-50%, -50%) scale(0.92); }
            50% { opacity: 0.9; transform: translate(-50%, -50%) scale(1.08); }
          }
          @keyframes botNudge {
            0%, 70%, 100% { transform: translateY(0) scale(1); }
            78% { transform: translateY(-6px) scale(1.03); }
            86% { transform: translateY(0) scale(1); }
            92% { transform: translateY(-3px) scale(1.015); }
          }
          @keyframes botPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.45), inset 0 1px 0 rgba(255,255,255,0.7); }
            50% { box-shadow: 0 0 0 10px rgba(251, 191, 36, 0), inset 0 1px 0 rgba(255,255,255,0.7); }
          }
          @keyframes botSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes botLogoFloat {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          @keyframes botDot {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.25); opacity: 0.75; }
          }
          @keyframes botLabelFloat {
            0%, 100% { transform: translateY(0); opacity: 1; }
            50% { transform: translateY(-3px); opacity: 0.92; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[65] bg-slate-950/20 backdrop-blur-[2px] sm:bg-transparent sm:backdrop-blur-0"
        onClick={closeChat}
        aria-hidden="true"
      />
      <div
        className={`fixed bottom-4 right-4 z-[70] flex ${panelSize} flex-col overflow-hidden rounded-[28px] border border-white/20 bg-[#0b1220] shadow-[0_30px_80px_rgba(2,6,23,0.55)] animate-[fadeInUp_0.25s_ease-out]`}
        role="dialog"
        aria-label="Analytics chatbot"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.18),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.12),transparent_28%)]" />
        <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/70 to-transparent" />

        <div className="relative border-b border-white/10 px-4 pb-3 pt-4 text-white">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-amber-300/50 to-yellow-500/20 blur-sm" />
                <div className="relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-amber-200/60">
                  <img src={eplLogo} alt="EPL" className="h-9 w-9 object-contain" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-[15px] font-semibold tracking-wide">Brownsville Analytics Bot</h2>
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" />
                    Live
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur">
              <button
                type="button"
                onClick={clearChat}
                className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
                title="Clear chat"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
                title={expanded ? 'Shrink' : 'Expand'}
              >
                {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={closeChat}
                className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
                title="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div
          ref={listRef}
          className="relative flex-1 space-y-3.5 overflow-y-auto bg-gradient-to-b from-[#111827]/80 via-[#f6f1e8] to-[#efe8dc] px-3.5 py-4"
        >
          {messages.map((message) =>
            message.role === 'user' ? (
              <UserMessage key={message.id} message={message} />
            ) : (
              <BotMessage key={message.id} message={message} />
            )
          )}
          {sending && (
            <div className="flex items-center gap-2 pl-11 text-xs font-medium text-slate-600">
              <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-amber-200/70 bg-white/80 px-2.5 shadow-sm backdrop-blur">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-600" />
                Analyzing venues…
              </span>
            </div>
          )}
        </div>

        <div className="relative border-t border-white/10 bg-[#0f172a]/95 px-3.5 py-3 backdrop-blur-xl">
          <div className="mb-2.5 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                disabled={sending}
                onClick={() => sendMessage(suggestion)}
                className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-medium text-slate-200 transition hover:border-amber-300/40 hover:bg-amber-300/10 hover:text-amber-100 disabled:opacity-50"
              >
                {suggestion}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="mb-2.5 flex w-full items-center justify-between rounded-xl border border-white/10 bg-gradient-to-r from-white/5 to-amber-300/5 px-3 py-2 text-left text-xs font-semibold text-slate-200 transition hover:border-amber-300/30"
          >
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              Structured query studio
            </span>
            <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>

          {showAdvanced && (
            <div className="mb-2.5 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-slate-950/50 p-3">
              <label className="col-span-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Start
                <input
                  type="date"
                  value={queryForm.startDate}
                  onChange={(e) => setQueryForm((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-slate-100 outline-none focus:border-amber-300/50"
                />
              </label>
              <label className="col-span-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                End
                <input
                  type="date"
                  value={queryForm.endDate}
                  onChange={(e) => setQueryForm((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-slate-100 outline-none focus:border-amber-300/50"
                />
              </label>
              <label className="col-span-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Venue
                <select
                  value={queryForm.venue}
                  onChange={(e) => setQueryForm((prev) => ({ ...prev, venue: e.target.value }))}
                  className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-slate-100 outline-none focus:border-amber-300/50"
                >
                  {DASHBOARD_CHAT_VENUES.map((venue) => (
                    <option key={venue.value} value={venue.value} className="bg-slate-900">
                      {venue.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="col-span-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Metric
                <select
                  value={queryForm.metric}
                  onChange={(e) => setQueryForm((prev) => ({ ...prev, metric: e.target.value }))}
                  className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-slate-100 outline-none focus:border-amber-300/50"
                >
                  {DASHBOARD_CHAT_METRICS.map((metric) => (
                    <option key={metric.value} value={metric.value} className="bg-slate-900">
                      {metric.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                disabled={sending}
                onClick={runStructuredQuery}
                className="col-span-2 rounded-xl bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400 px-3 py-2.5 text-xs font-bold tracking-wide text-slate-950 shadow-[0_10px_24px_rgba(251,191,36,0.25)] transition hover:brightness-105 disabled:opacity-60"
              >
                Run structured query
              </button>
            </div>
          )}

          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
          >
            <div className="relative flex-1">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder="Ask an analytics question…"
                className="max-h-28 min-h-[48px] w-full resize-none rounded-2xl border border-white/10 bg-white/5 px-3.5 py-3 text-sm text-slate-100 outline-none ring-amber-300/30 placeholder:text-slate-500 focus:border-amber-300/40 focus:ring-2"
                disabled={sending}
              />
            </div>
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 to-yellow-400 text-slate-950 shadow-[0_12px_28px_rgba(251,191,36,0.35)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Send message"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}
